import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getCloudinaryLibrary } from '@/utils/api';
import { Image, Video, Search, Loader2, Check, FolderOpen } from 'lucide-react';

export const CloudinaryLibrary = ({ open, onClose, onSelect, multiple = false, resourceType = 'all' }) => {
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState(resourceType === 'video' ? 'videos' : 'images');
  const [activeFolder, setActiveFolder] = useState('all');

  useEffect(() => {
    if (open) {
      loadLibrary();
      setSelectedItems([]);
    }
  }, [open]);

  const loadLibrary = async () => {
    setLoading(true);
    try {
      // Load images
      if (resourceType === 'all' || resourceType === 'image') {
        const imgData = await getCloudinaryLibrary('image');
        setImages(imgData.files || []);
      }
      
      // Load videos
      if (resourceType === 'all' || resourceType === 'video') {
        const vidData = await getCloudinaryLibrary('video');
        setVideos(vidData.files || []);
      }
    } catch (error) {
      console.error('Error loading Cloudinary library:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item) => {
    if (multiple) {
      setSelectedItems(prev => {
        const exists = prev.find(i => i.url === item.url);
        if (exists) {
          return prev.filter(i => i.url !== item.url);
        }
        return [...prev, item];
      });
    } else {
      setSelectedItems([item]);
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      onSelect(selectedItems);
    } else {
      onSelect(selectedItems[0]);
    }
    onClose();
  };

  const isSelected = (item) => selectedItems.some(i => i.url === item.url);

  const filterItems = (items) => {
    let filtered = items;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.public_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by folder
    if (activeFolder !== 'all') {
      filtered = filtered.filter(item => item.folder === activeFolder);
    }
    
    return filtered;
  };

  const getFolders = (items) => {
    const folders = new Set(items.map(item => item.folder));
    return ['all', ...Array.from(folders)];
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const allItems = activeTab === 'images' ? images : videos;
  const filteredItems = filterItems(allItems);
  const folders = getFolders(allItems);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5" />
            Biblioteca de Cloudinary
          </DialogTitle>
        </DialogHeader>

        {/* Search and filters */}
        <div className="flex gap-4 items-center py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar archivos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="cloudinary-search"
            />
          </div>
          
          {/* Folder filter */}
          <select 
            className="px-3 py-2 border rounded-md text-sm"
            value={activeFolder}
            onChange={(e) => setActiveFolder(e.target.value)}
            data-testid="cloudinary-folder-filter"
          >
            {folders.map(folder => (
              <option key={folder} value={folder}>
                {folder === 'all' ? 'Todas las carpetas' : folder}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs for images/videos */}
        {resourceType === 'all' && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="mb-2">
              <TabsTrigger value="images" className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Imágenes ({images.length})
              </TabsTrigger>
              <TabsTrigger value="videos" className="flex items-center gap-2">
                <Video className="w-4 h-4" />
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="images" className="flex-1 overflow-auto m-0">
              <MediaGrid 
                items={filterItems(images)} 
                loading={loading}
                onSelect={handleSelect}
                isSelected={isSelected}
                formatBytes={formatBytes}
              />
            </TabsContent>

            <TabsContent value="videos" className="flex-1 overflow-auto m-0">
              <MediaGrid 
                items={filterItems(videos)} 
                loading={loading}
                onSelect={handleSelect}
                isSelected={isSelected}
                formatBytes={formatBytes}
                isVideo
              />
            </TabsContent>
          </Tabs>
        )}

        {resourceType !== 'all' && (
          <div className="flex-1 overflow-auto">
            <MediaGrid 
              items={filteredItems} 
              loading={loading}
              onSelect={handleSelect}
              isSelected={isSelected}
              formatBytes={formatBytes}
              isVideo={resourceType === 'video'}
            />
          </div>
        )}

        {/* Footer with selection info and confirm button */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-slate-600">
            {selectedItems.length > 0 ? (
              <span>{selectedItems.length} archivo(s) seleccionado(s)</span>
            ) : (
              <span>Seleccioná {multiple ? 'uno o más archivos' : 'un archivo'}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cloudinary-cancel">
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirm} 
              disabled={selectedItems.length === 0}
              data-testid="cloudinary-confirm"
            >
              <Check className="w-4 h-4 mr-2" />
              Seleccionar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MediaGrid = ({ items, loading, onSelect, isSelected, formatBytes, isVideo = false }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        {isVideo ? <Video className="w-12 h-12 mb-2" /> : <Image className="w-12 h-12 mb-2" />}
        <p>No hay {isVideo ? 'videos' : 'imágenes'} disponibles</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 p-1">
      {items.map((item) => (
        <div
          key={item.public_id}
          onClick={() => onSelect(item)}
          className={`
            relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all
            ${isSelected(item) 
              ? 'border-blue-500 ring-2 ring-blue-200' 
              : 'border-transparent hover:border-slate-300'
            }
          `}
          data-testid={`cloudinary-item-${item.public_id}`}
        >
          {isVideo ? (
            <div className="w-full h-full bg-slate-800 flex items-center justify-center">
              <Video className="w-8 h-8 text-white" />
            </div>
          ) : (
            <img 
              src={item.url} 
              alt={item.filename}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
          
          {/* Selection indicator */}
          {isSelected(item) && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
          
          {/* Info overlay on hover */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 hover:opacity-100 transition-opacity">
            <p className="text-white text-xs truncate">{item.filename}</p>
            <p className="text-white/70 text-xs">{formatBytes(item.bytes)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CloudinaryLibrary;
